import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { normaliseLoginHint } from './loginHint';

const origin =
  typeof window !== 'undefined' ? window.location.origin : '';

const cognitoAuthConfig = {
  authority: import.meta.env.VITE_COGNITO_AUTHORITY as string,
  client_id: import.meta.env.VITE_COGNITO_CLIENT_ID as string,
  redirect_uri: import.meta.env.VITE_COGNITO_REDIRECT_URL as string,
  response_type: 'code' as const,
  scope: 'openid profile email',
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  silent_redirect_uri: `${origin}/auth/silent-callback`,
  /**
   * Cognito Hosted UI sends `frame-ancestors 'none'`, so iframe-based silent renew
   * (`signinSilent` / automatic silent renew) fails and spams CSP console errors.
   * Session continuity uses localStorage + refresh-token grant instead.
   */
  automaticSilentRenew: false,
};

export const userManager = new UserManager({ ...cognitoAuthConfig });

/**
 * `loginHint` is the email Google should preselect (Cognito forwards
 * `login_hint` to Google). Anything that is not email-shaped is ignored.
 */
export const signInWithGoogle = (loginHint?: string | null) => {
  const hint = normaliseLoginHint(loginHint);
  return userManager.signinRedirect({
    extraQueryParams: {
      identity_provider: 'GoogleOIDC',
      ...(hint ? { login_hint: hint } : {}),
    },
  });
};

export const handleAuthCallback = async () => {
  return userManager.signinRedirectCallback();
};

/**
 * Initiates Microsoft login by redirecting to the backend endpoint.
 * The backend should redirect to /auth/callback with tokens in the URL hash.
 */
export const signInWithMicrosoft = (loginHint?: string | null) => {
  const backendUrl = import.meta.env.VITE_BACKEND_URL as string;
  const frontendUrl = window.location.origin;
  const callbackUrl = `${frontendUrl}/auth/callback`;

  const hint = normaliseLoginHint(loginHint);
  const hintParam = hint ? `&login_hint=${encodeURIComponent(hint)}` : '';

  window.location.href = `${backendUrl}/microsoft_login?callback_uri=${encodeURIComponent(
    callbackUrl
  )}${hintParam}`;
};
